import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall } from '../utils/customerUtils'

interface ArchivedTask {
  id: number
  customer_id: number | null
  lead_id: number | null
  deal_id: number | null
  description: string
  due_date: string
  status: string
  priority: 'low' | 'medium' | 'high'
  created_at: string
  updated_at: string
  completed_at: string | null
  completed_by: number | null
  notes: string | null
  task_type: string | null
  follow_up_type: string | null
  customer_name: string | null
  customer_email: string | null
  lead_first_name: string | null
  lead_last_name: string | null
  lead_email: string | null
  deal_name: string | null
  completed_by_first_name: string | null
  completed_by_last_name: string | null
}

interface ArchivedTasksProps {
  isOpen: boolean
  onClose: () => void
  customerId?: number | null
  leadId?: number | null
  dealId?: number | null
}

const ArchivedTasks: React.FC<ArchivedTasksProps> = ({ 
  isOpen, 
  onClose, 
  customerId = null, 
  leadId = null, 
  dealId = null 
}) => {
  const { t } = useTranslation()
  const [tasks, setTasks] = useState<ArchivedTask[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    total: 0,
    count: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  })

  useEffect(() => {
    if (isOpen) {
      fetchArchivedTasks()
    }
  }, [isOpen, customerId, leadId, dealId])

  const fetchArchivedTasks = async (offset = 0) => {
    try {
      setLoading(true)
      setError(null)
      
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication required')
      }

      const params = new URLSearchParams({
        limit: '50',
        offset: offset.toString()
      })

      if (customerId) params.append('customer_id', customerId.toString())
      if (leadId) params.append('lead_id', leadId.toString())
      if (dealId) params.append('deal_id', dealId.toString())

      const response = await makeApiCall(`api/tasks/archived?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch archived tasks')
      }

      const data = await response.json()
      
      if (offset === 0) {
        setTasks(data.tasks || [])
      } else {
        setTasks(prev => [...prev, ...(data.tasks || [])])
      }
      
      setPagination(data.pagination || {
        total: 0,
        count: 0,
        limit: 50,
        offset: 0,
        hasMore: false
      })
    } catch (err) {
      console.error('Error fetching archived tasks:', err)
      setError(err instanceof Error ? err.message : 'Failed to load archived tasks')
    } finally {
      setLoading(false)
    }
  }

  const loadMore = () => {
    if (pagination.hasMore && !loading) {
      fetchArchivedTasks(pagination.offset + pagination.limit)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const getTaskTypeIcon = (taskType: string | null, followUpType: string | null) => {
    if (taskType === 'follow_up') {
      switch (followUpType) {
        case 'call': return '📞'
        case 'email': return '📧'
        case 'meeting': return '🤝'
        default: return '📋'
      }
    }
    return '✅'
  }

  const getAssociatedEntity = (task: ArchivedTask) => {
    if (task.customer_name) {
      return { type: 'Customer', name: task.customer_name, icon: '👤' }
    }
    if (task.lead_first_name || task.lead_last_name) {
      return { 
        type: 'Lead', 
        name: `${task.lead_first_name || ''} ${task.lead_last_name || ''}`.trim(), 
        icon: '🎯' 
      }
    }
    if (task.deal_name) {
      return { type: 'Deal', name: task.deal_name, icon: '💼' }
    }
    return { type: 'General', name: 'General Task', icon: '📋' }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              📁 Archived Tasks
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {pagination.total} completed tasks found
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading && tasks.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading archived tasks...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">⚠️ Error</div>
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
              <button
                onClick={() => fetchArchivedTasks(0)}
                className="mt-4 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">📁</div>
              <p className="text-gray-600 dark:text-gray-400">No archived tasks found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map((task) => {
                const entity = getAssociatedEntity(task)
                return (
                  <div
                    key={task.id}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-lg">
                            {getTaskTypeIcon(task.task_type, task.follow_up_type)}
                          </span>
                          <h3 className="font-medium text-gray-900 dark:text-white">
                            {task.description}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div>
                            <span className="font-medium">Associated with:</span>
                            <div className="flex items-center space-x-1 mt-1">
                              <span>{entity.icon}</span>
                              <span>{entity.name}</span>
                              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1 rounded">
                                {entity.type.toUpperCase()}
                              </span>
                            </div>
                          </div>

                          <div>
                            <span className="font-medium">Due Date:</span>
                            <div className="mt-1">
                              {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                            </div>
                          </div>

                          <div>
                            <span className="font-medium">Completed:</span>
                            <div className="mt-1">
                              {task.completed_at ? (
                                <>
                                  {new Date(task.completed_at).toLocaleDateString()}
                                  {task.completed_by_first_name && (
                                    <div className="text-xs">
                                      by {task.completed_by_first_name} {task.completed_by_last_name}
                                    </div>
                                  )}
                                </>
                              ) : (
                                'Unknown'
                              )}
                            </div>
                          </div>
                        </div>

                        {task.notes && (
                          <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded border">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes:</span>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-wrap">
                              {task.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}

              {pagination.hasMore && (
                <div className="text-center py-4">
                  <button
                    onClick={loadMore}
                    disabled={loading}
                    className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Loading...' : `Load More (${pagination.total - tasks.length} remaining)`}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ArchivedTasks
